import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router";
import { useAuth, AuthenticateWithRedirectCallback } from "@clerk/clerk-react";
import { PortalGuard } from "./components/PortalGuard";
import { ClerkTokenProvider } from "./components/ClerkTokenProvider";
import { isAuthV2 } from "@cliently/auth-client";
import { PortalGuardV2 } from "./auth-v2/PortalGuardV2";
import { AuthV2TokenProvider } from "./auth-v2/AuthV2TokenProvider";
import { RootRedirectV2 } from "./auth-v2/RootRedirectV2";

const ResetPasswordPageV2 = lazy(() =>
  import("./auth-v2/pages/ResetPasswordPage").then((m) => ({
    default: m.ResetPasswordPage,
  })),
);
const VerifyEmailPageV2 = lazy(() =>
  import("./auth-v2/pages/VerifyEmailPage").then((m) => ({
    default: m.VerifyEmailPage,
  })),
);
const TotpChallengePageV2 = lazy(() =>
  import("./auth-v2/pages/TotpChallengePage").then((m) => ({
    default: m.TotpChallengePage,
  })),
);
import { ClientLayout } from "./layouts/ClientLayout";
import { UpdatePrompt } from "./components/UpdatePrompt";
import { LoginPage } from "./pages/LoginPage";
import { DashboardPage } from "./pages/DashboardPage";
import { AppointmentsPage } from "./pages/AppointmentsPage";
import { PaymentsPage } from "./pages/PaymentsPage";
import { ProfilePage } from "./pages/ProfilePage";
import { SettingsPage } from "./pages/SettingsPage";
import { AboutPage } from "./pages/AboutPage";
import { RequestsPage } from "./pages/RequestsPage";
import { ChatPage } from "./pages/ChatPage";
import { ChatConversationPage } from "./pages/ChatConversationPage";
import { ObjectivesPage } from "./pages/ObjectivesPage";
import { ProgramsPage } from "./pages/ProgramsPage";
import { ServicesPage } from "./pages/ServicesPage";
import { NumericMetricsPage } from "./pages/NumericMetricsPage";
import { TimelinePage } from "./pages/TimelinePage";



const RootRedirectClerk = () => {
  const { isSignedIn, isLoaded } = useAuth();

  if (!isLoaded) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="border-primary h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    );
  }

  if (isSignedIn) {
    return <Navigate to="/dashboard" replace />;
  }

  return <LoginPage />;
}

// Sélection module-level pour éviter les hooks conditionnels.
const RootRedirect = isAuthV2() ? RootRedirectV2 : RootRedirectClerk;
const Guard = isAuthV2() ? PortalGuardV2 : PortalGuard;
const TokenProvider = isAuthV2() ? AuthV2TokenProvider : ClerkTokenProvider;

const RouteFallback = () => (
  <div className="flex min-h-screen items-center justify-center">
    <div className="border-primary h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
  </div>
);

export const App = () => {
  return (
    <TokenProvider>
      <UpdatePrompt />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          {!isAuthV2() && (
            <Route
              path="/sso-callback"
              element={<AuthenticateWithRedirectCallback />}
            />
          )}
          {isAuthV2() && (
            <>
              <Route path="/auth/2fa" element={<TotpChallengePageV2 />} />
              <Route
                path="/auth/reset-password"
                element={<ResetPasswordPageV2 />}
              />
              <Route
                path="/auth/verify-email"
                element={<VerifyEmailPageV2 />}
              />
            </>
          )}

          <Route element={<Guard />}>
            <Route element={<ClientLayout />}>
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/objectives" element={<ObjectivesPage />} />
              <Route path="/metrics" element={<NumericMetricsPage />} />
              <Route path="/programs" element={<ProgramsPage />} />
              <Route path="/appointments" element={<AppointmentsPage />} />
              <Route path="/services" element={<ServicesPage />} />
              <Route path="/payments" element={<PaymentsPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/requests" element={<RequestsPage />} />
              <Route path="/timeline" element={<TimelinePage />} />
              <Route path="/chat" element={<ChatPage />} />
              <Route path="/chat/:conversationId" element={<ChatConversationPage />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </TokenProvider>
  );
}
